import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { canSeeAdmin, isViewer, isViewerPlus } from '../lib/permissions';
import { requestActivityNavigation } from '../lib/activityEditGuard';
import NotificationListener from './NotificationListener';
import CreateLeadModal from './CreateLeadModal';

const mainNav = [
  ['/', 'Today'],
  ['/attention', 'Needs Attention'],
  ['/leads', 'Leads'],
  ['/activity', 'Activity'],
  ['/reports', 'Reports'],
  ['/trash', 'Trash'],
];

const mobilePrimaryNav = [
  ['/', '⌂', 'Today'],
  ['/attention', '!', 'Attention'],
  ['/leads', '◎', 'Leads'],
  ['/activity', '▥', 'Activity'],
];

function guardedNavProps(to, afterNavigate) {
  return {
    onClick: (event) => {
      if (!requestActivityNavigation(to)) {
        event.preventDefault();
        return;
      }
      afterNavigate?.();
    },
  };
}

function mobilePageContext(pathname) {
  if (/^\/leads\/[^/]+$/.test(pathname) && pathname !== '/leads/not-a-lead') return { title: 'Lead Record', backTo: '/leads', backLabel: 'Leads' };
  if (pathname === '/leads/not-a-lead') return { title: 'Not a Lead', backTo: '/leads', backLabel: 'Leads' };
  if (pathname === '/') return { title: 'Today' };
  if (pathname.startsWith('/attention')) return { title: 'Needs Attention' };
  if (pathname === '/leads') return { title: 'Leads' };
  if (pathname.startsWith('/activity')) return { title: 'Activity' };
  if (pathname.startsWith('/reports')) return { title: 'Reports', backTo: '__more__', backLabel: 'More' };
  if (pathname.startsWith('/trash')) return { title: 'Trash', backTo: '__more__', backLabel: 'More' };
  if (pathname.startsWith('/admin/integrations')) return { title: 'Integrations', backTo: '__more__', backLabel: 'More' };
  if (pathname.startsWith('/admin/audit')) return { title: 'Audit', backTo: '__more__', backLabel: 'More' };
  if (pathname.startsWith('/admin/settings')) return { title: 'Settings', backTo: '__more__', backLabel: 'More' };
  return { title: 'Lead Tracker' };
}

export default function AppShell() {
  const { workspace, profile, user, role, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const [sidebarCreateOpen, setSidebarCreateOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => globalThis.matchMedia?.('(max-width: 820px)').matches ?? false);
  const display = profile?.display_name || profile?.email || user?.email || 'User';
  const readOnly = isViewer(role);
  const viewerPlus = isViewerPlus(role);
  const readOnlyLabel = viewerPlus ? 'Viewer Plus · Read only' : 'Viewer · Read only';
  const showNotifications = !readOnly || viewerPlus;

  useEffect(() => setMobileMoreOpen(false), [location.pathname]);

  useEffect(() => {
    const media = globalThis.matchMedia?.('(max-width: 820px)');
    if (!media) return undefined;
    const sync = () => setIsMobile(media.matches);
    sync();
    media.addEventListener?.('change', sync);
    return () => media.removeEventListener?.('change', sync);
  }, []);

  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (!document.querySelector('.activity-cell-changed')) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  const moreActive = location.pathname.startsWith('/reports') || location.pathname.startsWith('/trash') || location.pathname.startsWith('/admin');
  const mobileContext = mobilePageContext(location.pathname);

  function handleMobileBack() {
    if (mobileContext.backTo === '__more__') {
      setMobileMoreOpen(true);
      return;
    }
    if (mobileContext.backTo && requestActivityNavigation(mobileContext.backTo)) navigate(mobileContext.backTo);
  }

  function handleTodayActivityEdit() {
    const to = viewerPlus ? '/activity?mode=day&motion=outbound' : '/activity?mode=day&motion=outbound&edit=1';
    if (requestActivityNavigation(to)) navigate(to);
  }

  function handleSidebarCreateLead() {
    if (!requestActivityNavigation('/leads')) return;
    if (location.pathname !== '/leads') navigate('/leads');
    setSidebarCreateOpen(true);
  }

  return (
    <div className="app-shell" data-read-only={readOnly ? 'true' : 'false'}>
      <aside className="sidebar desktop-sidebar">
        <div className="brand-row"><div className="brand-mark small">LT</div><div><strong>Lead Tracker</strong><span>{workspace?.name}</span></div></div>
        <nav className="nav-stack">
          {mainNav.map(([to, label]) => to === '/leads' ? (
            <div className="nav-item-action-row" key={to}>
              <NavLink to={to} {...guardedNavProps(to)} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>{label}</NavLink>
              {!readOnly && <button type="button" className="sidebar-lead-add" onClick={handleSidebarCreateLead} aria-label="Create lead">+</button>}
            </div>
          ) : (
            <NavLink key={to} to={to} end={to === '/'} {...guardedNavProps(to)} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>{label}</NavLink>
          ))}
        </nav>
        {canSeeAdmin(role) && (
          <div className="admin-nav">
            <span className="nav-caption">Administration</span>
            {!viewerPlus && <NavLink to="/admin/team" {...guardedNavProps('/admin/team')} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>Team</NavLink>}
            <NavLink to="/admin/integrations" {...guardedNavProps('/admin/integrations')} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>Integrations</NavLink>
            <NavLink to="/admin/audit" {...guardedNavProps('/admin/audit')} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>Audit</NavLink>
            <NavLink to="/admin/settings" {...guardedNavProps('/admin/settings')} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>Settings</NavLink>
          </div>
        )}
        <div className="sidebar-user">
          {showNotifications && !isMobile && <NotificationListener readOnly={viewerPlus} />}
          {readOnly && <span className="pill">{readOnlyLabel}</span>}
          <strong>{display}</strong>
          <button className="text-button" onClick={signOut}>Sign out</button>
        </div>
      </aside>

      <header className="mobile-topbar">
        <div className="mobile-page-context">
          {mobileContext.backTo ? <button type="button" className="mobile-back-button" onClick={handleMobileBack} aria-label={`Back to ${mobileContext.backLabel}`}><span aria-hidden="true">‹</span></button> : <div className="brand-mark small">LT</div>}
          <div><strong>{mobileContext.title}</strong><span>{mobileContext.backTo ? mobileContext.backLabel : workspace?.name}</span></div>
        </div>
        <div className="mobile-topbar-actions">
          {showNotifications && isMobile && <NotificationListener compact readOnly={viewerPlus} />}
          {(!readOnly || viewerPlus) && isMobile && location.pathname === '/' && <button type="button" className="mobile-today-edit-button" onClick={handleTodayActivityEdit} aria-label="Edit activity"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4Z"/></svg><span>Edit activity</span></button>}
        </div>
      </header>

      <main className="main-content">
        {readOnly && <div className="info-message">{viewerPlus ? 'Viewer Plus · Read only' : 'Viewer access — read only. You can browse CRM data, but changes are disabled.'}</div>}
        <Outlet />
      </main>

      <nav className="mobile-bottom-nav" aria-label="Primary navigation">
        {mobilePrimaryNav.map(([to, icon, label]) => (
          <NavLink key={to} to={to} end={to === '/'} {...guardedNavProps(to)} className={({ isActive }) => `mobile-bottom-item${isActive ? ' active' : ''}`}>
            <span className="mobile-nav-icon" aria-hidden="true">{icon}</span><span>{label}</span>
          </NavLink>
        ))}
        <button className={`mobile-bottom-item${moreActive || mobileMoreOpen ? ' active' : ''}`} type="button" onClick={() => setMobileMoreOpen(true)} aria-expanded={mobileMoreOpen}>
          <span className="mobile-nav-icon" aria-hidden="true">•••</span><span>More</span>
        </button>
      </nav>

      {mobileMoreOpen && <div className="mobile-more-backdrop" role="presentation" onMouseDown={e => e.target === e.currentTarget && setMobileMoreOpen(false)}>
        <section className="mobile-more-sheet" role="dialog" aria-modal="true" aria-label="More navigation">
          <div className="mobile-sheet-handle" />
          <div className="mobile-more-head"><div><strong>{display}</strong><span>{readOnly ? readOnlyLabel : workspace?.name}</span></div><button className="icon-button" onClick={() => setMobileMoreOpen(false)} aria-label="Close">×</button></div>
          <div className="mobile-more-grid">
            <NavLink to="/reports" {...guardedNavProps('/reports', () => setMobileMoreOpen(false))} className="mobile-more-link"><span>▤</span><div><strong>Reports</strong><small>Performance and conversion</small></div></NavLink>
            <NavLink to="/trash" {...guardedNavProps('/trash', () => setMobileMoreOpen(false))} className="mobile-more-link"><span>⌫</span><div><strong>Trash</strong><small>Deleted records</small></div></NavLink>
            {canSeeAdmin(role) && <>
              {!viewerPlus && <NavLink to="/admin/team" {...guardedNavProps('/admin/team', () => setMobileMoreOpen(false))} className="mobile-more-link"><span>♙</span><div><strong>Team</strong><small>People and access</small></div></NavLink>}
              <NavLink to="/admin/integrations" {...guardedNavProps('/admin/integrations', () => setMobileMoreOpen(false))} className="mobile-more-link"><span>↔</span><div><strong>Integrations</strong><small>Connected services</small></div></NavLink>
              <NavLink to="/admin/audit" {...guardedNavProps('/admin/audit', () => setMobileMoreOpen(false))} className="mobile-more-link"><span>◷</span><div><strong>Audit</strong><small>Change history</small></div></NavLink>
              <NavLink to="/admin/settings" {...guardedNavProps('/admin/settings', () => setMobileMoreOpen(false))} className="mobile-more-link"><span>⚙</span><div><strong>Settings</strong><small>Workspace controls</small></div></NavLink>
            </>}
          </div>
          <button className="mobile-signout" type="button" onClick={signOut}>Sign out</button>
        </section>
      </div>}

      {!readOnly && <CreateLeadModal open={sidebarCreateOpen} onClose={() => setSidebarCreateOpen(false)} onCreated={(created) => { const leadId = created?.id || created?.lead_id; setSidebarCreateOpen(false); if (leadId) navigate(`/leads/${leadId}`); else navigate('/leads'); }} />}
    </div>
  );
}